import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  MessageSquare,
  BookOpen,
  Tag,
  Hash,
  Calendar,
  Settings2,
  Users,
  MessagesSquare,
  FolderOpen,
  Sparkles,
  UserCircle,
  Terminal,
  Flag,
  ClipboardList,
  TrendingUp,
  BarChart3,
  Activity,
  Zap,
  ShieldCheck,
  Coins,
} from 'lucide-react';
import { useAuth } from '@/lib/auth/AuthProvider';

type NavItem = {
  name: string;
  href: string;
  icon: React.ElementType;
  color?: 'purple' | 'rose' | 'coral' | 'teal';
  superAdminOnly?: boolean;
};

type NavSection = {
  title: string;
  items: NavItem[];
  superAdminOnly?: boolean;
};

const navigation: (NavItem | NavSection)[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, color: 'purple' },
  { name: 'Outputs', href: '/outputs', icon: FileText, color: 'rose' },
  { name: 'History', href: '/history', icon: MessageSquare, color: 'coral' },
  {
    title: 'Long-term',
    items: [
      { name: 'Reviews', href: '/reviews', icon: ClipboardList },
      { name: 'Insights', href: '/insights', icon: TrendingUp },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { name: 'Keywords', href: '/keywords', icon: Tag, color: 'purple' },
      { name: 'Projects', href: '/projects', icon: Hash, color: 'rose' },
      { name: 'Instructions', href: '/instructions', icon: BookOpen, color: 'coral' },
      { name: 'Schedule', href: '/schedule', icon: Calendar, color: 'teal' },
      { name: 'Preferences', href: '/preferences', icon: Settings2, color: 'purple' },
    ],
  },
  {
    title: 'Sources',
    items: [
      { name: 'People', href: '/people', icon: Users, color: 'rose' },
      { name: 'Conversations', href: '/conversations', icon: MessagesSquare, color: 'coral' },
      { name: 'Locations', href: '/locations', icon: FolderOpen, color: 'teal' },
    ],
  },
  {
    title: 'Analytics',
    items: [
      { name: 'My Analytics', href: '/analytics', icon: Activity, color: 'teal' },
      { name: 'Admin Analytics', href: '/analytics/admin', icon: BarChart3, color: 'purple', superAdminOnly: true },
      { name: 'Token Usage', href: '/analytics/tokens', icon: Coins, color: 'rose', superAdminOnly: true },
    ],
  },
  {
    title: 'Account',
    items: [
      { name: 'Settings', href: '/settings', icon: UserCircle, color: 'rose' },
      { name: 'Permissions', href: '/permissions', icon: ShieldCheck, color: 'teal' },
    ],
  },
  {
    title: 'Super Admin',
    superAdminOnly: true,
    items: [
      { name: 'Admin', href: '/admin', icon: Terminal, color: 'coral', superAdminOnly: true },
      { name: 'User Admin', href: '/admin/users', icon: Users, color: 'rose', superAdminOnly: true },
      { name: 'Feature Flags', href: '/feature-flags', icon: Flag, color: 'teal', superAdminOnly: true },
      { name: 'Analytics', href: '/analytics/admin', icon: BarChart3, color: 'purple', superAdminOnly: true },
      { name: 'Agent Diagnostics', href: '/agent-diagnostics', icon: Zap, color: 'rose', superAdminOnly: true },
    ],
  },
];

function isSection(item: NavItem | NavSection): item is NavSection {
  return 'title' in item;
}

const colorClasses = {
  purple: {
    icon: 'text-primary-500',
    activeIcon: 'text-primary-600 dark:text-primary-400',
    activeText: 'text-primary-700 dark:text-primary-300',
    activeBg: 'bg-primary-500/10',
  },
  rose: {
    icon: 'text-rose-500',
    activeIcon: 'text-rose-600 dark:text-rose-400',
    activeText: 'text-rose-700 dark:text-rose-300',
    activeBg: 'bg-rose-500/10',
  },
  coral: {
    icon: 'text-coral-500',
    activeIcon: 'text-coral-600 dark:text-coral-400',
    activeText: 'text-coral-700 dark:text-coral-300',
    activeBg: 'bg-coral-500/10',
  },
  teal: {
    icon: 'text-teal-500',
    activeIcon: 'text-teal-600 dark:text-teal-400',
    activeText: 'text-teal-700 dark:text-teal-300',
    activeBg: 'bg-teal-500/10',
  },
};

export function Sidebar() {
  const { isInTeams, isSuperAdmin } = useAuth();
  
  // Filter navigation based on super admin status
  const filteredNavigation = navigation
    .filter(item => {
      if (isSection(item)) {
        return !item.superAdminOnly || isSuperAdmin;
      }
      return !item.superAdminOnly || isSuperAdmin;
    })
    .map(item => {
      if (isSection(item)) {
        return {
          ...item,
          items: item.items.filter(subItem => !subItem.superAdminOnly || isSuperAdmin),
        };
      }
      return item;
    })
    // Remove empty sections
    .filter(item => !isSection(item) || item.items.length > 0);

  const renderNavItem = (item: NavItem, compact = false) => {
    const colors = colorClasses[item.color || 'purple'];
    
    return (
      <NavLink
        key={item.name}
        to={item.href}
        className={({ isActive }) =>
          `group relative flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-all duration-200 ${
            compact ? 'py-2.5' : ''
          } ${
            isActive
              ? `${colors.activeBg} ${colors.activeText}`
              : 'text-dark-500 dark:text-dark-400 hover:bg-dark-100 dark:hover:bg-dark-800/50 hover:text-dark-900 dark:hover:text-white'
          }`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-primary-500`} />
            )}
            <item.icon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} ${isActive ? colors.activeIcon : colors.icon} transition-colors`} />
            <span>{item.name}</span>
          </>
        )}
      </NavLink>
    );
  };

  const renderNavigation = (compact = false) => (
    <>
      {filteredNavigation.map((item, index) => {
        if (isSection(item)) {
          return (
            <div key={item.title} className={index > 0 ? 'mt-6' : 'mt-4'}>
              <div className={`px-4 mb-2 ${compact ? 'text-[10px]' : 'text-xs'} font-bold uppercase tracking-wider text-dark-400 dark:text-dark-500`}>
                {item.title}
              </div>
              <div className="space-y-1">
                {item.items.map((subItem) => renderNavItem(subItem, compact))}
              </div>
            </div>
          );
        }
        return (
          <div key={item.name} className={index > 0 ? 'mt-1' : ''}>
            {renderNavItem(item, compact)}
          </div>
        );
      })}
    </>
  );

  // In Teams, show a more compact sidebar since tabs handle primary navigation
  if (isInTeams) {
    return (
      <aside className="hidden w-60 flex-shrink-0 bg-white dark:bg-dark-900/95 border-r border-dark-100 dark:border-dark-800 lg:block">
        <div className="flex h-full flex-col">
          {/* Compact logo */}
          <div className="flex h-16 items-center gap-3 border-b border-dark-100 dark:border-dark-800 px-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 shadow-lg shadow-primary-500/20">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <span className="font-display text-lg font-bold text-dark-900 dark:text-white">
              Impact Agent
            </span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto space-y-1 p-4">
            {renderNavigation(true)}
          </nav>
        </div>
      </aside>
    );
  }

  // Full sidebar for standalone web app
  return (
    <aside className="hidden w-72 flex-shrink-0 bg-white dark:bg-dark-900/95 border-r border-dark-100 dark:border-dark-800 lg:block">
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-20 items-center gap-4 border-b border-dark-100 dark:border-dark-800 px-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-500 shadow-lg shadow-primary-500/20">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <h1 className="font-display text-xl font-bold text-dark-900 dark:text-white">
            Impact Agent
          </h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto space-y-1 p-4">
          {renderNavigation(false)}
        </nav>

        {/* Footer Tip */}
        <div className="p-4">
          <div className="rounded-2xl bg-[#E8E0F5] dark:bg-primary-500/15 p-4 border border-primary-200/50 dark:border-primary-500/20">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">💡</span>
              <p className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                Pro Tip
              </p>
            </div>
            <p className="text-xs text-dark-600 dark:text-dark-400 leading-relaxed">
              Update your keywords and projects to help the agent find relevant work.
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
}
